import api from "./api";
import { i18n } from "../main";
import { compressData } from "../utils/array";
import { produce } from "immer";
import { ProcessTypes } from "../../../api.app/controllers/constants";

const initialState = {
  // id -> status/error
  action: { status: {}, error: {} },
  processDesigns: [], // listed process designs
  draggingProcessDesign: null,
  searchCancelTokenSrc: null,
};
export default {
  namespaced: true,
  state: () => ({ ...initialState }),
  mutations: {},
  actions: {
    async status({ state }, data) {
      state.action.status = produce(state.action.status, (status) => {
        Object.assign(status, data); // Merge incoming data into the status
      });
    },
    async error({ state }, data) {
      state.action.error = produce(state.action.error, (error) => {
        Object.assign(error, data); // Merge incoming data into the error
      });
    },
    async create({ commit, state, rootGetters, rootState, dispatch }, { type, teamId, design } = {}) {
      try {
        await dispatch("status", { creating: "creating" });
        await dispatch("error", { creating: null });

        const names = i18n.global.tm("names.design");
        const title = design?.title ?? names[(Math.random() * Object.keys(names).length).toFixed()];
        let payload = {
          Sheet1: {
            team_id: teamId,
            type,
            title,
          },
        };
        if (design?.rowMap) {
          payload.Sheet1.rowMap = design?.rowMap || {};
          payload.Sheet1.columnMap = design?.columnMap || {};
          payload.Sheet1.arrows = design?.arrows || {};
          payload.Sheet1.cellsContent = design?.cellsContent || {};
          payload.Sheet1.mergedCells = design?.mergedCells || {};
        }
        if (design?.sapData) {
          payload.Sheet1.sapData = design?.sapData || {};
        }

        const compressedData = compressData(payload);
        const resp = await api.post(`designs`, { compressedData, teamId });

        await dispatch("status", { creating: "created" });
        return resp;
      } catch (exp) {
        await dispatch("status", { creating: "creation-failed" });
        await dispatch("error", { creating: exp });
        console.log(exp);
        if (exp.response.status == 402) {
          dispatch("quotaReached", exp, { root: true });
        } else {
          console.error(exp);

          dispatch(
            "addToast",
            {
              severity: "warn",
              life: 3000,
              ...i18n.global.tm("processDesigner.create.failed"),
            },
            { root: true }
          );
        }

        throw exp;
      }
    },
    async createGrid({ commit, state, rootGetters, rootState, dispatch }, { type, teamId } = {}) {
      try {
        await dispatch("status", { creating: "creating" });
        await dispatch("error", { creating: null });

        const names = i18n.global.tm("names.design");
        const title = names[(Math.random() * Object.keys(names).length).toFixed()];

        const payload = {
          Sheet1: {
            team_id: teamId,
            type,
            title,
          },
        };
        const compressedData = compressData(payload);
        const resp = await api.post(`designs`, { compressedData, teamId });
        await dispatch("status", { creating: "created" });
        return resp;
      } catch (exp) {
        console.log(exp);
        await dispatch("status", { creating: "creation-failed" });
        await dispatch("error", { creating: exp });

        if (exp.response.status == 402) {
          dispatch("quotaReached", exp, { root: true });
        } else {
          console.error(exp);

          dispatch(
            "addToast",
            {
              severity: "warn",
              life: 3000,
              ...i18n.global.tm("processDesigner.create.failed"),
            },
            { root: true }
          );
        }

        throw exp;
      }
    },
    async delete({ commit, state, rootGetters, dispatch }, ids) {
      try {
        await dispatch("status", { deleting: "deleting" });
        await dispatch("error", { deleting: null });
        await dispatch("table/deleteTablesInDesign", { ids }, { root: true });
        const resp = await api.post("designs/delete", { designIds: ids });

        await dispatch("status", { deleting: "deleted" });

        return resp;
      } catch (exp) {
        await dispatch("status", { deleting: "deletion-failed" });
        await dispatch("error", { deleting: null });

        if (exp.response.status == 402) {
          dispatch("quotaReached", exp, { root: true });
        } else {
          console.error(exp);

          dispatch(
            "addToast",
            {
              severity: "warn",
              life: 3000,
              ...i18n.global.tm("processDesigner.delete.failed"),
            },
            { root: true }
          );
        }

        throw exp;
      }
    },
    async get({ commit, state, rootGetters, dispatch }, id) {
      try {
        return await api.get(`designs/${id}`);
      } catch (exp) {
        console.error(exp);
        throw exp;
      }
    },
    async getPermissions({ commit, state, rootGetters, dispatch }, id) {
      try {
        return await api.get(`designs/${id}/get-permissions`);
      } catch (exp) {
        console.error(exp);
        throw exp;
      }
    },
    async getDesignPermissions({ commit, state, rootGetters, dispatch }, id) {
      try {
        const res = await api.get(`designs/${id}/get-design-permissions`);
        return res.data.permissions;
      } catch (exp) {
        console.error(exp);
        throw exp;
      }
    },
    async getTeamPermission({ commit, state, rootGetters, dispatch }, id) {
      try {
        const res = await api.get(`designs/${id}/get-team-permissions`);
        return res.data.permissions;
      } catch (exp) {
        console.error(exp);
        throw exp;
      }
    },
    async set({ commit, state, rootGetters, dispatch }, pdesign) {
      try {
        const resp = await (pdesign.id ? api.patch : api.post)(`designs${pdesign.id ? "/" + pdesign.id : ""}`, pdesign);

        return resp;
      } catch (exp) {
        console.error(exp);
        throw exp;
      }
    },
    async createProcessFromSAP({ dispatch, state, commit }, { controllingArea, cycle, date }) {
      try {
        await dispatch("status", { creating: "loading" });
        const result = await api.get(`sap/?cycle=${controllingArea}${cycle}&date=${date}`);
        const withSapData = { ...result.data, sapData: { controllingArea, cycle, date } };
        dispatch("processes/addProcess", { design: withSapData, type: ProcessTypes.Cycle }, { root: true });
      } catch (error) {
        dispatch(
          "pdesign/setError",
          {
            show: true,
            type: "warning",
            summary: "Process Not Found",
            detail: "No process matches the provided details. Please check and try again.",
            buttons: [{ text: "Close", type: "cancel" }],
          },
          { root: true }
        );
      } finally {
        await dispatch("status", { creating: "loaded" });
      }
    },
    async updateProcessPermissions({ dispatch, state }, { designId, permissions }) {
      try {
        await dispatch("status", { creating: "loading" });
        await api.patch(`designs/${designId}/update-permissions`, { permissions });
      } catch (error) {
        dispatch(
          "pdesign/setError",
          {
            show: true,
            type: "warning",
            summary: "Process Not Found",
            detail: "No process matches the provided details. Please check and try again.",
            buttons: [{ text: "Close", type: "cancel" }],
          },
          { root: true }
        );
      } finally {
        await dispatch("status", { creating: "loaded" });
      }
    },
    async resetState({ state }) {
      Object.assign(state, { ...initialState });
    },
  },
  getters: {
    list: (state) => () => {
      return state.processDesigns || [];
    },
    get: (state) => (id) => {
      return (state.processDesigns.filter((each) => each.id == id) || [null])[0];
    },
    listBy: (state) => (attr, value) => {
      value = value.toLowerCase();
      return state.processDesigns.filter(
        (each) =>
          each[attr]
            .toLowerCase()
            .split(" ")
            .filter((word) => word.startsWith(value)).length
      );
    },
  },
};
